import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import classNames from "classnames";

import { makeStyles } from "@material-ui/core/styles";
import { AppBar, Button, Toolbar, Dialog, useMediaQuery, useTheme } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import KingBedOutlinedIcon from "@material-ui/icons/KingBedOutlined";
import queryString from "query-string";

import FindBookingModal from "components/FindBookingModal";
import AuthCTA from "components/AuthCTA";
import CommunicationPreferences from "components/CommunicationPreferences";
import LogoSection from "./Components/LogoSection";
import DesktopNavActions from "./Components/DesktopNavActions";
import MobileMenu from "./Components/MobileMenu";
import { setTriggerSignInSignUpModal } from "features/user/userSlice";
import { BOOKING_SOURCES, SIGN_IN_MODAL } from "utils/constants";
import { useSearchParams, useSignInWithFirebaseToken, useSignOutUser } from "hooks";
import {
  checkIsInIframe,
  requestParentToSignIn,
  requestParentToSignUp,
} from "utils/marketingSiteMessageUtils";
import IframeNavActions from "./Components/IframeNavActions";
import Routes from "config/routes";
import { preserveSourceInQueryParams, selectCurrentSource } from "utils/sourceUtils";
import { preserveThemeInQueryParams } from "utils/themeUtils";
import ConfirmEmailModal from "components/ConfirmEmailModal";

const useStyles = makeStyles((theme) => ({
  logoAppBar: {
    position: "static",
    backgroundColor: theme.palette.background.componentBackground1,
    padding: "0 !important", // Material UI bug see https://github.com/mui-org/material-ui/issues/17353
    boxShadow: theme.navBarShadow,
  },
  logoToolbar: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    margin: "0px auto",
    width: "95%",
    maxWidth: theme.contentSize.maxContentWidth,
    minHeight: "76px",
    paddingLeft: "0px",
    paddingRight: "0px",
    [theme.breakpoints.down(theme.breakpoints.values.tablet)]: {
      width: "100%",
      minHeight: "auto",
      paddingTop: "1rem",
      paddingBottom: "1rem",
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  logoContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  navbarLogo: {
    height: "27px",
    [theme.breakpoints.up("tablet")]: {
      height: "33px",
    },
  },
  navbarMultiLogo: {
    height: "13px",
    [theme.breakpoints.up("tablet")]: {
      height: "13px",
    },
  },
  navbarLogoCentered: {
    [theme.breakpoints.up("tablet")]: {
      position: "absolute",
      transform: "translateX(-50%)",
      left: "50%",
    },
  },
  plusIcon: {
    display: "flex",
    alignItems: "center",
  },
  menu: {
    "& li, a": {
      minHeight: "32px",
    },
  },
  menuDivider: {
    margin: "5px 10px",
  },
  navActionsContainer: {
    display: "flex",
    alignItems: "center",
  },
  navActionContainer: {
    display: "flex",
    alignItems: "center",
  },
  iframeModeContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: "auto",
  },
  dialogPaper: {
    width: "580px",
    margin: "auto",
  },
  closeButton: {
    position: "absolute",
    right: "1rem",
    top: "1rem",
    cursor: "pointer",
    color: theme.palette.textSecondary,
  },
  menuButton: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    border: "1px solid",
    color: theme.palette.text.primary,
    borderColor: theme.palette.text.primary,
    "&:hover": {
      color: theme.palette.background.default,
      backgroundColor: theme.palette.grey[900],
    },
  },
  menuText: {
    paddingTop: "1px",
    marginRight: "8px",
    marginLeft: "6px",
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    height: "28px",
    width: "28px",
    borderRadius: "14px",
    marginRight: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  initials: {
    color: theme.palette.text.onDark.primary,
  },
  hidden: {
    display: "none",
  },
}));

const Navbar = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const tabletUpScreen = useMediaQuery(theme.breakpoints.up("tablet"));

  // Redux state
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.user.authUser);
  const mobileAppUser = useSelector((state) => state.user.mobileAppUser);
  const preservedQueryParams = useSearchParams();
  const hasChosenCommunicationPreferences = useSelector(
    (state) => state.user.hasChosenCommunicationPreferences
  );
  const triggerSignInSignUpModal = useSelector((state) => state.user.triggerSignInSignUpModal);

  // Hooks
  const signOutUser = useSignOutUser();
  const { signInWithFbToken } = useSignInWithFirebaseToken();

  // Local state
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const [signUpModalOpen, setSignUpModalOpen] = useState(false);
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(
    authUser && authUser.emailVerified && !hasChosenCommunicationPreferences
  );
  const [confirmEmailModalOpen, setConfirmEmailModalOpen] = useState(
    authUser && !authUser.emailVerified
  );
  const [findBookingModalOpen, setFindBookingModalOpen] = useState(false);

  // Derived state
  const bookingSource = useSelector(selectCurrentSource);
  const isSohoMode = bookingSource.id === BOOKING_SOURCES.SOHO.id;

  // Event handlers
  const handleSignOut = () => {
    signOutUser();
  };

  const isInIframe = checkIsInIframe();

  const shouldHideNavBar = !isInIframe && !bookingSource.showNavbar;

  const toggleSignInModal = () => {
    if (isInIframe) {
      // Request parent to open popup
      requestParentToSignIn();
    } else {
      setSignInModalOpen(!signInModalOpen);
    }
  };

  const toggleSignUpModal = () => {
    if (isInIframe) {
      // Request parent to open popup
      requestParentToSignUp();
    } else {
      setSignUpModalOpen(!signUpModalOpen);
    }
  };

  const toggleFindBookingModal = () => {
    setFindBookingModalOpen(!findBookingModalOpen);
  };

  const closeAllModals = () => {
    setSignInModalOpen(false);
    setSignUpModalOpen(false);
    setPreferencesModalOpen(false);
    setConfirmEmailModalOpen(false);
  };

  const handleRedirectToHome = () => {
    const preservedSource = preserveSourceInQueryParams(preservedQueryParams);
    const preservedTheme = preserveThemeInQueryParams(preservedQueryParams);

    const preservedParams = {
      ...preservedTheme,
      ...preservedSource,
    };

    const searchString =
      Object.keys(preservedParams).length > 0 ? `?${queryString.stringify(preservedParams)}` : "";

    return Routes.PLAN_STAY + searchString;
  };

  // Add postMessage listener for auth results from Parent site Popup
  useEffect(() => {
    const handleAuthMessage = (event) => {
      // Validate origin for security
      if (event.origin !== process.env.REACT_APP_MOLLIES_IFRAME_HOST) return;

      if (event.data.type === "AUTH_COMPLETE" && event.data.success) {
        // If we have auth data, process it
        if (event.data.authUser && event.data.fbToken) {
          signInWithFbToken(event.data.fbToken);
        }
      } else if (event.data.type === "AUTH_ERROR") {
        console.error("Auth error from popup:", event.data.error);
        // Handle error (show notification, etc.)
        if (props.onNotifOpen) {
          props.onNotifOpen({
            type: "error",
            message: event.data.error || "Authentication failed",
          });
        }
      }
    };

    if (isInIframe) {
      window.addEventListener("message", handleAuthMessage);
      return () => window.removeEventListener("message", handleAuthMessage);
    }
  }, [isInIframe, props]);

  useEffect(() => {
    if (authUser && authUser.emailVerified && !hasChosenCommunicationPreferences) {
      setPreferencesModalOpen(true);
    } else {
      setPreferencesModalOpen(false);
    }
  }, [hasChosenCommunicationPreferences, authUser?.emailVerified]);

  useEffect(() => {
    setConfirmEmailModalOpen(authUser && !authUser.emailVerified);
  }, [authUser?.emailVerified, authUser]);

  useEffect(() => {
    if (triggerSignInSignUpModal === SIGN_IN_MODAL.SIGN_UP) {
      setSignUpModalOpen(true);
      dispatch(setTriggerSignInSignUpModal(SIGN_IN_MODAL.NONE));
    } else if (triggerSignInSignUpModal === SIGN_IN_MODAL.SIGN_IN) {
      setSignInModalOpen(true);
      dispatch(setTriggerSignInSignUpModal(SIGN_IN_MODAL.NONE));
    }
  }, [triggerSignInSignUpModal, dispatch]);

  // Render
  if (shouldHideNavBar) {
    return null;
  }

  return (
    <AppBar className={classes.logoAppBar}>
      <Toolbar className={classes.logoToolbar}>
        {!isInIframe && (
          <LogoSection
            classes={classes}
            isSohoMode={isSohoMode}
            centerLogo={props.centerLogo}
            to={handleRedirectToHome()}
          />
        )}
        {isInIframe && location.pathname === Routes.ACCOUNT && (
          <Button
            startIcon={<KingBedOutlinedIcon fontSize="large" />}
            component={Link}
            color="primary"
            to={Routes.PLAN_STAY}>
            Book
          </Button>
        )}
        {!isSohoMode && (
          <div
            className={classNames(
              classes.navActionsContainer,
              mobileAppUser ? classes.hidden : null,
              isInIframe ? classes.iframeModeContainer : null
            )}>
            {props.withProfile && isInIframe && (
              <IframeNavActions
                authUser={authUser}
                onSignOut={handleSignOut}
                onFindBooking={toggleFindBookingModal}
                onSignUp={toggleSignUpModal}
                onSignIn={toggleSignInModal}
              />
            )}
            {props.withProfile && !isInIframe && (
              <div className={classes.navActionContainer}>
                {tabletUpScreen && (
                  <DesktopNavActions
                    authUser={authUser}
                    onSignOut={handleSignOut}
                    onFindBooking={toggleFindBookingModal}
                    onSignUp={toggleSignUpModal}
                    onSignIn={toggleSignInModal}
                  />
                )}

                {!tabletUpScreen && (
                  <MobileMenu
                    classes={classes}
                    authUser={authUser}
                    tabletUpScreen={tabletUpScreen}
                    onSignOut={handleSignOut}
                    onSignIn={toggleSignInModal}
                    onSignUp={toggleSignUpModal}
                    onFindBooking={toggleFindBookingModal}
                  />
                )}

                <FindBookingModal
                  open={findBookingModalOpen}
                  setOpen={setFindBookingModalOpen}
                  onNotifOpen={props.onNotifOpen}
                />

                <Dialog
                  open={
                    signInModalOpen ||
                    signUpModalOpen ||
                    preferencesModalOpen ||
                    confirmEmailModalOpen
                  }
                  onClose={closeAllModals}
                  PaperProps={{ elevation: 8, className: classes.dialogPaper }}>
                  <CloseIcon className={classes.closeButton} onClick={closeAllModals} />
                  {signInModalOpen ? (
                    <AuthCTA
                      logIn={true}
                      onNotifOpen={props.onNotifOpen}
                      splitLinks={true}
                      authCompleted={() => setSignInModalOpen(false)}
                    />
                  ) : signUpModalOpen ? (
                    <AuthCTA
                      signUp={true}
                      onNotifOpen={props.onNotifOpen}
                      splitLinks={true}
                      authCompleted={() => setSignUpModalOpen(false)}
                    />
                  ) : preferencesModalOpen ? (
                    <CommunicationPreferences
                      isModal
                      heading="Please confirm your choices"
                      onNotifOpen={props.onNotifOpen}
                    />
                  ) : confirmEmailModalOpen ? (
                    <ConfirmEmailModal
                      open={confirmEmailModalOpen}
                      onNotifOpen={props.onNotifOpen}
                      onSuccess={() => setConfirmEmailModalOpen(false)}
                    />
                  ) : null}
                </Dialog>
              </div>
            )}
          </div>
        )}
      </Toolbar>
    </AppBar>
  );
};

Navbar.propTypes = {
  centerLogo: PropTypes.bool,
  withProfile: PropTypes.bool,
  onNotifOpen: PropTypes.func,
};

export default Navbar;
